import {
    ProvisionSimulationFigureDto
} from '../../model/dto/api/provision-simulation.dto';
import {
    provisionSimulationTechnicalLabel
} from '../../enum/provision-simulation-technical-label';

/**
 * The ONE place the simulation screen turns a delivered figure into the string a German reader
 * sees — and the line between FORMATTING (allowed, required) and CALCULATING (forbidden).
 *
 * ── WHAT THIS FILE MAY DO
 *
 * The endpoint delivers every figure as a STRING it has already produced: `"3600.00"` with unit
 * `EUR`, `"2026-09-01"` with no unit, `"36"` with unit `Monate`, `"MONTHLY_PREMIUM"` with no unit.
 * German eyes need `3.600,00 €`, `01.09.2026`, `36 Monate`, `Monatsbeiträge`. That mapping is
 * presentation — it changes how a value is spelled, never what it is — and Spezifikation §5.5 asks
 * for it by name ("Datumsformat DD.MM.YYYY, Beträge 1.234,56 €").
 *
 * ── WHAT THIS FILE MUST NEVER DO
 *
 * No arithmetic on a business value. No sum over figures, no per-mille to per-cent, no monthly to
 * annual, no rounding of a business number to make a column line up, no derivation of a value that
 * was not delivered. The simulation exists because the productive engine is the only place a
 * commission amount may be computed; a helper that "just" adds two euro figures would put a second
 * number on screen next to the one the run pays out — the exact duplication the endpoint was built
 * to avoid. `parseFloat` appears below ONLY to hand the number to `toLocaleString`, and the parsed
 * value is discarded if it is not finite, in which case the raw string is shown unchanged.
 *
 * ── AN UNPARSEABLE VALUE IS SHOWN, NOT SWALLOWED
 *
 * If a figure with unit `EUR` ever arrives as something `parseFloat` cannot read, the raw text is
 * rendered. A blank cell on a screen that explains money reads as "there is nothing here"; the raw
 * text reads as "something is off with this value", which is the true statement.
 */

/** `YYYY-MM-DD`, anchored — the only date shape the endpoint produces. */
const ISO_DATE: RegExp = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A DECIMAL number written with a dot, anchored — `0.5000`, `-12.5`.
 *
 * The decimal point is the whole discriminator, and that is deliberate. Unit-less figures carry two
 * very different things: decimal business numbers such as a factor (`"0.5000"`) that a German reader
 * expects with a comma, and bare IDENTIFIERS such as `"9000001003"` for the liability type. Turning
 * the second into `9.000.001.003` would make an id unreadable and unsearchable, which is worse than
 * leaving a factor with a dot. Integers therefore stay untouched; only a value that already has a
 * fractional part is re-punctuated.
 */
const DECIMAL_NUMBER: RegExp = /^-?\d+\.\d+$/;

/**
 * German rendering of one delivered figure value.
 *
 * @param value the value exactly as delivered; `null` stays a dash
 * @param unit  the delivered unit — `EUR`, `%`, `Monate`, `Jahre`, or absent
 */
export function formatProvisionSimulationValue(value: string, unit?: string): string {

    if (value == null || value === '') {
        return '—';
    }

    if (unit === 'EUR') {
        return formatDecimal(value, 2, 2) + ' €';
    }

    if (unit === '%') {
        return formatDecimal(value, 2, 2) + ' %';
    }

    if (unit) {
        // Counts of months and years: no forced decimals, the unit spelled out behind the number.
        return formatDecimal(value, 0, 4) + ' ' + unit;
    }

    if (ISO_DATE.test(value)) {
        return formatIsoDate(value);
    }

    if (DECIMAL_NUMBER.test(value)) {
        // The delivered number of decimals is KEPT, as minimum and as maximum: the engine chose that
        // scale (a factor arrives with four places) and neither padding nor trimming it here would
        // be formatting any more — it would be changing what the value says about its own precision.
        const decimals: number = value.length - value.indexOf('.') - 1;
        return formatDecimal(value, decimals, decimals);
    }

    // No unit and not a date: either a German sentence the server already wrote, or a technical
    // constant name. The label map translates only what a backend caption exists for and returns
    // anything else unchanged — it never guesses.
    return provisionSimulationTechnicalLabel(value);
}

/** German rendering of a whole figure, used wherever label and value travel together. */
export function formatProvisionSimulationFigure(figure: ProvisionSimulationFigureDto): string {
    return formatProvisionSimulationValue(figure?.value, figure?.unit);
}

/**
 * `YYYY-MM-DD` -> `DD.MM.YYYY`, by re-ordering the three delivered parts.
 *
 * Deliberately NOT via `new Date(...)`: a date-only string is parsed as UTC midnight, so reading the
 * day back out in local time moves it BACKWARDS for every reader WEST of UTC — a contract start of
 * `2026-09-01` renders as 31.08.2026 in New York. The key dates of a commission calculation are
 * exactly the values that must not shift by a day, so the string is re-arranged and never
 * interpreted.
 *
 * The direction matters more than it looks: east of UTC the same broken implementation is CORRECT,
 * so a developer in Berlin can neither see the bug nor catch it with a test. Measured — the
 * `new Date` variant passes every spec below under `TZ=Europe/Berlin` and fails three of them under
 * `TZ=America/New_York`. The re-arrangement holds in both.
 *
 * An ABSENT date is the dash, and the empty string counts as absent — the same answer
 * {@link formatProvisionSimulationValue} gives for the same input. It answered `''` with `''` while
 * answering `null` with the dash, which no caller reaches through the value formatter but every
 * direct caller of this exported function would have: a blank cell where the module's whole
 * argument is that a blank cell reads as "there is nothing here".
 */
export function formatIsoDate(value: string): string {

    if (!value) {
        return '—';
    }

    if (!ISO_DATE.test(value)) {
        return value;
    }

    const parts: string[] = value.split('-');

    return parts[2] + '.' + parts[1] + '.' + parts[0];
}

/**
 * German number formatting of a delivered numeric string.
 *
 * Returns the raw text unchanged when it is not a finite number — see the "shown, not swallowed"
 * paragraph above.
 */
function formatDecimal(value: string, minimumFractionDigits: number,
                       maximumFractionDigits: number): string {

    const parsed: number = parseFloat(value);

    if (!isFinite(parsed)) {
        return value;
    }

    return parsed.toLocaleString('de-DE', {minimumFractionDigits, maximumFractionDigits});
}
